import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box, Button, Card, CardContent, TextField, Typography, Link,
  InputAdornment, Snackbar, Alert
} from '@mui/material';

import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCachedUser } from '../utils/api'; // fallback if login() doesn't return a user

export default function Login() {
  const { login } = useAuth(); // use the AuthContext
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setSnackbar({ open: true, message: 'Email and password are required', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      // 🔹 login should return user details including role (but some implementations don't)
      const userFromLogin = await login(email, password);

      // 🔹 fallback: if login() didn't return the user, try the cached user (set by AuthContext/utils)
      const cached = getCachedUser ? getCachedUser() : null;
      const user = userFromLogin ?? cached;

      setSnackbar({ open: true, message: 'Login successful!', severity: 'success' });

      // 🔹 redirect based on role (safe optional chaining)
      setTimeout(() => {
        if (user?.role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate("/user/dashboard");
        }
      }, 800);

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Login failed. Please check credentials.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        bgcolor: 'background.default',
        background: 'radial-gradient(ellipse at top left, #0ea5e9 0%, #0f172a 100%)',
        transition: 'background 0.5s',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 40 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.7, type: 'spring' }}
      >
        <Card
          sx={{
            width: 300,
            p: 3,
            borderRadius: 4,
            boxShadow: '0 0 40px 0 #00b4d8, 0 2px 20px 0 #0ea5e9',
            border: '2px solid #00b4d8',
            background: 'rgba(30, 41, 59, 0.95)',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 4,
              boxShadow: '0 0 60px 10px #00b4d8',
              opacity: 0.15,
              zIndex: 0,
            },
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 1 }}>
            <Box display="flex" justifyContent="center" mb={2}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #00B4D8, #7B2FF2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 24px 6px #00b4d8',
                  }}
                >
                  <LockIcon sx={{ color: 'white', fontSize: 36 }} />
                </Box>
              </motion.div>
            </Box>
            <Typography variant="h5" align="center" gutterBottom fontWeight="bold" color="#fff">
              FentrixBank Login
            </Typography>
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                margin="normal"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  input: { color: '#fff' },
                  label: { color: '#b0b8c1' },
                  fieldset: { borderColor: '#00b4d8' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#00b4d8' },
                    '&:hover fieldset': { borderColor: '#7B2FF2' },
                    '&.Mui-focused fieldset': { borderColor: '#7B2FF2' },
                  },
                  mb: 2,
                }}
              />
              <TextField
                fullWidth
                type="password"
                label="Password"
                variant="outlined"
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  input: { color: '#fff' },
                  label: { color: '#b0b8c1' },
                  fieldset: { borderColor: '#00b4d8' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#00b4d8' },
                    '&:hover fieldset': { borderColor: '#7B2FF2' },
                    '&.Mui-focused fieldset': { borderColor: '#7B2FF2' },
                  },
                  mb: 2,
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                component={motion.button}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                sx={{
                  mt: 2,
                  py: 1.2,
                  fontWeight: 'bold',
                  fontSize: 16,
                  background: 'linear-gradient(135deg, #00B4D8, #7B2FF2)',
                  boxShadow: '0 0 16px 2px #00b4d8',
                  borderRadius: 3,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7B2FF2, #00B4D8)',
                    boxShadow: '0 0 32px 4px #7B2FF2',
                  },
                }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
            <Typography align="center" mt={2} color="#b0b8c1">
              Don't have an account?{' '}
              <Link
                onClick={() => navigate('/register')}
                underline="hover"
                sx={{
                  color: '#00B4D8',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  '&:hover': { color: '#7B2FF2' },
                }}
              >
                Register
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </motion.div>

      {/* ✅ Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ fontWeight: 'bold' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}